import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const field = "rounded-xl bg-white border-[#103080]/20 h-11";
const OTHER = "other";

export default function EntryDialog({ entry, tasks, userId, onClose, onSaved }) {
  const [f, setF] = useState({ date: "", hours: "1", task_id: OTHER, label: "", category: "Admin", note: "" });
  const editing = Boolean(entry?.id);

  useEffect(() => {
    if (entry) {
      setF({ date: entry.date, hours: entry.minutes ? String(entry.minutes / 60) : "1", task_id: entry.task_id || OTHER,
        label: entry.task_id ? "" : entry.label || "", category: entry.category || "Admin", note: entry.note || "" });
    }
  }, [entry]);

  const save = async (e) => {
    e.preventDefault();
    const minutes = Math.round(Number(f.hours) * 60);
    const body = { date: f.date, minutes, note: f.note, user_id: userId || null,
      ...(f.task_id === OTHER ? { label: f.label, category: f.category } : { task_id: f.task_id }) };
    try {
      if (editing) await api.put(`/timesheet/entries/${entry.id}`, body);
      else await api.post("/timesheet/entries", body);
      onClose();
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    await api.delete(`/timesheet/entries/${entry.id}`).catch((err) => toast.error(formatApiError(err)));
    onClose();
    onSaved();
  };

  return (
    <Dialog open={Boolean(entry)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl bg-[#FDFBF7] border-[#103080]/10 sm:max-w-md" data-testid="time-entry-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-brand text-2xl text-[#103080]">{editing ? "Edit time" : "Log time"}</DialogTitle>
          <DialogDescription>Link the time to a job you worked on, or describe other work.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Date</Label>
              <Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={field} data-testid="time-entry-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Hours</Label>
              <Input type="number" min={0.25} max={24} step={0.25} required value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} className={field} data-testid="time-entry-hours" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Job</Label>
            <Select value={f.task_id} onValueChange={(v) => setF({ ...f, task_id: v })}>
              <SelectTrigger className={field} data-testid="time-entry-job-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={OTHER}>Other work (not a client job)</SelectItem>
                {tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.client_name ? `${t.client_name} · ` : ""}{t.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {f.task_id === OTHER && (
            <div className="grid grid-cols-[1fr_140px] gap-3">
              <div className="space-y-1.5">
                <Label>What did you work on?</Label>
                <Input required value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} className={field} placeholder="e.g. Team meeting" data-testid="time-entry-label" />
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                  <SelectTrigger className={field} data-testid="time-entry-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(CATEGORIES).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Note (optional)</Label>
            <Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={field} data-testid="time-entry-note" />
          </div>
          <div className="flex items-center justify-between pt-2">
            {editing ? (
              <button type="button" onClick={remove} data-testid="time-entry-delete" className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 transition-colors duration-200"><Trash2 size={15} /> Delete</button>
            ) : <span />}
            <button type="submit" data-testid="time-entry-save"
              className="rounded-full bg-[#103080] text-white font-medium px-7 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200">Save</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
